import React, { useContext, useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Td,
  Th,
  Box,
  Flex,
  useToast,
  Icon,
} from "@chakra-ui/react";
import { GrEdit } from "react-icons/gr";
import { GoTriangleDown, GoTriangleUp } from "react-icons/go";
import { ProductsContext } from "../Context/ProductsContext";
import useFetch from "../../hooks/useFetch";
import TdType from "../Common/Table/TdType";
import { currencyFormatter } from "../../Utils/formatUtils";
import CustomTBody from "../Common/Table/CustomTBody";
import { createSkeleton } from "../../Utils/utils";
import Pagination from "../Common/Layout/Pagination";
import { UsersContext } from "../Context/UsersContext";
import { userIsAllowed } from "../../Utils/userIsAllowed";

function ProductsTable() {
  const {
    setProducts,
    products,
    isLoadingProduct,
    maxPagination,
    currentPage,
    setPagination,
    setOrderKey,
  } = useContext(ProductsContext);
  const { currentUser } = useContext(UsersContext);
  const toast = useToast();
  const { put } = useFetch();
  const [order, setOrder] = useState("productName");
  const [orderDirection, setOrderDirection] = useState("ASC");

  const handleSwitch = (product, e) => {
    const value = e.target.checked;
    const name = e.target.id;
    let newProducts = [...products];

    let index = newProducts.findIndex(
      (newProduct) => product.id === newProduct.id
    );
    newProducts[index][name] = value;
    setProducts(newProducts);

    const formData = new FormData();
    formData.append(name, value ? 1 : 0);

    put(`/products/${product.id}`, formData)
      .then(async () => {
        toast({
          title: "Articulo editado con éxito.",
          description: "Se ha actualizado el artículo.",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      })
      .catch((e) => {
        toast({
          title: "Se ha producido un error.",
          description:
            e?.message || "Contacte con el administrador del sistema.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      });
  };

  const handleSelectedPage = (page) => {
    setPagination(page);
  };

  const handleSelectedOrder = (key, direction) => {
    let newDirection = "ASC";
    if (key === order) {
      newDirection = orderDirection === "ASC" ? "DESC" : "ASC";
      setOrderDirection(newDirection);
    } else {
      setOrder(key);
      setOrderDirection("ASC");
    }
    setOrderKey(key, newDirection);
  };

  const createRows = () => {
    let formattedRows = products.map((product) => {
      return [
        {
          item: <TdType text={`${product.brand} ${product.name}`} />,
        },
        {
          item: (
            <TdType
              text={`${product.category ? product.category : ""} ${
                product.subCategory ? product.subCategory : ""
              }`}
            />
          ),
        },
        {
          item: <TdType text={`${currencyFormatter(product.cost)}`} />,
        },
        userIsAllowed(currentUser, [17])
          ? {
              item: <TdType text={`${product.stock}`} />,
            }
          : {
              item: <TdType text={`-`} customStyle={{ textAlign: "left" }} />,
            },
        userIsAllowed(currentUser, [17])
          ? {
              item: <TdType text={`${product.minStock}`} />,
            }
          : {
              item: <TdType text={`-`} customStyle={{ textAlign: "left" }} />,
            },
        {
          tdStyle: { textAlign: "center" },
          item: (
            <TdType
              inputType="switch"
              id="sale"
              isChecked={product.sale}
              onChange={(e) => handleSwitch(product, e)}
            />
          ),
        },
        {
          tdStyle: { textAlign: "center" },
          item: (
            <TdType
              inputType="switch"
              id="active"
              isChecked={product.active}
              onChange={(e) => handleSwitch(product, e)}
            />
          ),
        },
        userIsAllowed(currentUser, [1])
          ? {
              item: (
                <TdType
                  link={`/articulos/${product.id}`}
                  inputType="icon"
                  icon={GrEdit}
                  title="Editar"
                />
              ),
            }
          : {
              item: <TdType text={`-`} customStyle={{ textAlign: "center" }} />,
            },
      ];
    });
    return formattedRows;
  };

  return (
    <>
      <Box overflowX={"scroll"} pb={"0.5rem"}>
        <Table variant="striped" colorScheme="gray" fontSize="0.8em">
          <Thead>
            <Tr>
              <TitleItem
                name="productName"
                title="Artículo"
                handleClick={handleSelectedOrder}
                currentOrderKey={order}
                currentOrderDirection={orderDirection}
                w={"20%"}
              />
              <TitleItem
                name="categoryOrder"
                title="Categorías"
                handleClick={handleSelectedOrder}
                currentOrderKey={order}
                currentOrderDirection={orderDirection}
                w={"20%"}
              />
              <TitleItem
                name="cost"
                title="Costo"
                handleClick={handleSelectedOrder}
                currentOrderKey={order}
                currentOrderDirection={orderDirection}
              />
              <TitleItem
                name="stock"
                title="Stock"
                handleClick={handleSelectedOrder}
                currentOrderKey={order}
                currentOrderDirection={orderDirection}
              />
              <TitleItem
                name="minstock"
                title="Stock Mín."
                handleClick={handleSelectedOrder}
                currentOrderKey={order}
                currentOrderDirection={orderDirection}
              />
              <TitleItem
                name="sale"
                title="Oportunidad"
                handleClick={handleSelectedOrder}
                currentOrderKey={order}
                currentOrderDirection={orderDirection}
                textAlign={"center"}
              />
              <TitleItem
                name="active"
                title="Activo"
                handleClick={handleSelectedOrder}
                currentOrderKey={order}
                currentOrderDirection={orderDirection}
                textAlign={"center"}
              />
              <Th fontSize="0.8em" textAlign="center">
                Acciones
              </Th>
            </Tr>
          </Thead>
          {isLoadingProduct ? (
            <CustomTBody rows={[createSkeleton(8)]} />
          ) : products.length ? (
            <CustomTBody rows={createRows()} />
          ) : (
            <Tbody>
              <Tr>
                <Td colSpan="100%">
                  <Box padding="1rem 0">No se encontraron resultados</Box>
                </Td>
              </Tr>
            </Tbody>
          )}
        </Table>
      </Box>
      <Flex justifyContent={"center"} py={"2rem"}>
        <Pagination
          max={maxPagination}
          currentPage={currentPage}
          handleSelectedPage={handleSelectedPage}
        />
      </Flex>
    </>
  );
}

function TitleItem({
  title,
  name,
  currentOrderDirection,
  currentOrderKey,
  handleClick,
  w = "10%",
  textAlign = "left",
}) {
  const handleTitleClick = () => {
    const newOrderDirection = currentOrderDirection === "ASC" ? "DESC" : "ASC";
    handleClick(name, newOrderDirection);
  };

  return (
    <Th
      fontSize="0.8em"
      onClick={() => handleTitleClick()}
      w={w}
      textAlign={textAlign}
      cursor={"pointer"}
    >
      {currentOrderKey === name ? (
        <Flex justifyContent={"space-between"}>
          {title}
          {currentOrderDirection === "ASC" && <Icon as={GoTriangleDown} />}
          {currentOrderDirection === "DESC" && <Icon as={GoTriangleUp} />}
        </Flex>
      ) : (
        <>{title}</>
      )}
    </Th>
  );
}

export default ProductsTable;
